import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ErrorState } from '@eview/core/store/states/error.state';
import { ApiServerError } from '../store/actions/error.actions';
import { Injectable } from '@angular/core';
import { SessionExpiredService } from '../services/session-expired.service';
import { AuthService } from '../auth';
import { logAuthError, logAuthWarn } from '../auth/auth-debug.helpers';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  private readonly excludes = [
    'users/me',
    'notifications_ex',
    'tags',
    'attributes',
    'assets/i18n',
    'amazonaws.com',
    'X-Amz-Signature'
  ];

  /** Public/auth endpoints where a 401 means bad credentials, not a dead session */
  private readonly authEndpoints = [
    'oauth/token',
    'auth/login',
    '/2fa/',
    'register',
    'passwordreset'
  ];

  /**
   * Anonymous catalog / public map reads. A stale token here must not kick
   * the visitor to login — guests can read published reports without grants.
   */
  private readonly publicReadSnippets = [
    '/tags',
    '/forms',
    '/posts',
    '/media',
    '/layers',
    '/config'
  ];

  private isExcluded(req: HttpRequest<any>): boolean {
    return this.excludes.some(e => req.url.includes(e));
  }

  private isAuthEndpoint(req: HttpRequest<any>): boolean {
    return this.authEndpoints.some(e => req.url.includes(e));
  }

  private isPublicReadEndpoint(req: HttpRequest<any>): boolean {
    const method = (req.method || 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      return false;
    }
    const url = req.url || '';
    return this.publicReadSnippets.some(s => url.includes(s));
  }

  private hadSession(): boolean {
    try {
      return !!(this.authService as any)?._token;
    } catch {
      return false;
    }
  }

  /**
   * 403 = lack of permission (stay logged in).
   * Never treat "not allowed" as session failure even if status is wrong.
   */
  private isPermissionDenial(err: HttpErrorResponse): boolean {
    if (err.status === 403) {
      return true;
    }
    const msg = String(
      err.error?.errors?.[0]?.message ||
        err.error?.message ||
        err.message ||
        ''
    ).toLowerCase();
    return (
      msg.includes('not allowed') ||
      msg.includes('not authorized') ||
      msg.includes('forbidden') ||
      msg.includes('capability') ||
      msg.includes('form action')
    );
  }

  private isSessionInvalid401(err: HttpErrorResponse): boolean {
    if (err.status !== 401) {
      return false;
    }
    if (this.isPermissionDenial(err)) {
      return false;
    }
    return true;
  }

  constructor(
    private store: Store<ErrorState>,
    private sessionExpiredService: SessionExpiredService,
    private authService: AuthService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (
            this.isSessionInvalid401(err) &&
            !this.isAuthEndpoint(req) &&
            this.hadSession()
          ) {
            if (this.isPublicReadEndpoint(req)) {
              // Stale token on public map/catalog: drop identity, stay as guest
              logAuthWarn('error.interceptor: 401 on public read, clearing token', {
                url: req.url
              });
              try {
                this.authService.clearToken('error.interceptor: 401 on public read');
              } catch {
                /* ignore */
              }
              return throwError(() => err);
            }
            // Inactive user / revoked token on protected routes
            logAuthError('error.interceptor: session invalid 401, force logout', err, {
              url: req.url
            });
            this.sessionExpiredService.forceSessionEnd();
            return throwError(() => err);
          }

          const networkStatus =
            err.status === 0 && err.statusText == 'Unknown Error'
              ? false
              : true;
          if (
            !this.isExcluded(req) &&
            networkStatus &&
            (err.status !== 401 || this.isPermissionDenial(err))
          ) {
            this.store.dispatch(new ApiServerError(err));
          }
          return networkStatus ? throwError(() => err) : of(null);
        }
        return throwError(() => err);
      })
    );
  }
}
